import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

// Simple interfaces compatible with Angular 7
interface DownloadProgress {
  backupId: number;
  progress: number;
  downloadedBytes: number;
  totalBytes: number;
  status: string;
  filename?: string;
  error?: string;
  canResume?: boolean;
}

interface DownloadCallbacks {
  onProgress?: (progress: DownloadProgress) => void;
  onComplete?: (backupId: number) => void;
  onError?: (backupId: number, error: string) => void;
  onCancel?: (backupId: number) => void;
}

@Injectable({
  providedIn: 'root'
})
export class DownloadManagerService implements OnDestroy {

  private readonly WORKER_URL = '/assets/workers/download.worker.js';
  private readonly WORKER_VERSION = '2.0.0';

  // Worker management
  private workers = new Map<number, Worker>();
  private downloadStates = new Map<number, DownloadProgress>();
  private callbacks = new Map<number, DownloadCallbacks>();

  // Reactive state management
  private downloadsSubject = new BehaviorSubject<Map<number, DownloadProgress>>(new Map());
  public downloads$: Observable<Map<number, DownloadProgress>> = this.downloadsSubject.asObservable();

  // Background monitoring
  private backgroundCheckInterval: any = null;
  private isInitialized = false;

  constructor() {
    this.initializeService();
  }

  /**
   * Initialize the service
   */
  private initializeService(): void {
    if (this.isInitialized) { return; }

    this.isInitialized = true;
    this.startBackgroundMonitoring();
    this.checkForActiveDownloads();

    // Handle page visibility changes
    const self = this;
    if (document.addEventListener) {
      document.addEventListener('visibilitychange', function() {
        if (!document.hidden) {
          self.checkForActiveDownloads();
        }
      });
    }
  }

  /**
   * Start a new download
   */
  public startDownload(
    backupId: number,
    url: string,
    filename: string,
    callbacks?: DownloadCallbacks
  ): void {

    if (this.isDownloadActive(backupId)) {
      // console.log('Download already active for backup ' + backupId);
      return;
    }

    const initialState: DownloadProgress = {
      backupId: backupId,
      progress: 0,
      downloadedBytes: 0,
      totalBytes: 0,
      status: 'downloading',
      filename: filename
    };

    this.updateDownloadState(backupId, initialState);

    if (callbacks) {
      this.callbacks.set(backupId, callbacks);
    }

    this.createWorker(backupId, url, filename);
  }

  /**
   * Resume a download
   */
  public resumeDownload(
    backupId: number,
    url: string,
    filename: string,
    callbacks?: DownloadCallbacks
  ): void {
    const existingState = this.downloadStates.get(backupId);
    const startByte = existingState ? existingState.downloadedBytes : 0;

    // console.log('Resuming download for backup ' + backupId + ' from byte ' + startByte);

    if (callbacks) {
      this.callbacks.set(backupId, callbacks);
    }

    this.createWorker(backupId, url, filename, startByte);
  }

  /**
   * Pause a download
   */
  public pauseDownload(backupId: number): void {
    const worker = this.workers.get(backupId);
    if (worker) {
      worker.postMessage({ type: 'pause' });
      this.updateDownloadState(backupId, { status: 'paused' });
    }
  }

  /**
   * Cancel a download
   */
  public cancelDownload(backupId: number): void {
    const worker = this.workers.get(backupId);
    if (worker) {
      worker.postMessage({ type: 'cancel' });
      worker.terminate();
      this.workers.delete(backupId);
    }

    this.updateDownloadState(backupId, { status: 'cancelled' });
    this.downloadStates.delete(backupId);
    this.callbacks.delete(backupId);
    this.notifyStateChange();

    const callbacks = this.callbacks.get(backupId);
    if (callbacks && callbacks.onCancel) {
      callbacks.onCancel(backupId);
    }
  }

  /**
   * Check if download is active
   */
  public isDownloadActive(backupId: number): boolean {
    const state = this.downloadStates.get(backupId);
    return (state && state.status === 'downloading') || this.workers.has(backupId);
  }

  /**
   * Get download progress
   */
  public getDownloadProgress(backupId: number): DownloadProgress | null {
    return this.downloadStates.get(backupId) || null;
  }

  /**
   * Get all downloads
   */
  public getAllDownloads(): Observable<Map<number, DownloadProgress>> {
    return this.downloads$;
  }

  /**
   * Check for active downloads
   */
  public hasActiveDownloads(): boolean {
    const states = Array.from(this.downloadStates.values());
    for (let i = 0; i < states.length; i++) {
      if (states[i].status === 'downloading') {
        return true;
      }
    }
    return false;
  }

  /**
   * Get download statistics
   */
  public getDownloadStats(): any {
    const states = Array.from(this.downloadStates.values());

    let active = 0;
    let completed = 0;
    let paused = 0;
    let errors = 0;

    for (let i = 0; i < states.length; i++) {
      const state = states[i];
      if (state.status === 'downloading') { active++; }
      if (state.status === 'completed') { completed++; }
      if (state.status === 'paused') { paused++; }
      if (state.status === 'error') { errors++; }
    }

    return {
      total: states.length,
      active: active,
      completed: completed,
      paused: paused,
      errors: errors
    };
  }

  /**
   * Reset all downloads
   */
  public resetAllDownloads(): void {
    const backupIds = Array.from(this.downloadStates.keys());
    for (let i = 0; i < backupIds.length; i++) {
      this.cancelDownload(backupIds[i]);
    }
  }

  /**
   * Service cleanup
   */
  public ngOnDestroy(): void {
    this.stopBackgroundMonitoring();

    const workers = Array.from(this.workers.values());
    for (let i = 0; i < workers.length; i++) {
      workers[i].terminate();
    }
    this.workers.clear();

    this.downloadStates.clear();
    this.callbacks.clear();
  }

  // Private Methods

  /**
   * Create worker
   */
  private createWorker(
    backupId: number,
    url: string,
    filename: string,
    startByte: number = 0
  ): void {

    this.cleanupWorker(backupId);

    const workerUrl = this.WORKER_URL + '?v=' + this.WORKER_VERSION + '&t=' + Date.now();
    const worker = new Worker(workerUrl);

    this.workers.set(backupId, worker);

    const self = this;
    worker.onmessage = function(event) {
      self.handleWorkerMessage(backupId, event);
    };

    worker.onerror = function(error) {
      self.handleWorkerError(backupId, error);
    };

    worker.postMessage({
      type: 'start',
      url: url,
      filename: filename,
      start: startByte,
      backupId: backupId
    });
  }

  /**
   * Handle worker messages
   */
  private handleWorkerMessage(backupId: number, event: MessageEvent): void {
    const data = event.data;

    if (data.type === 'progress') {
      this.handleProgressUpdate(backupId, data);
    } else if (data.type === 'complete') {
      this.handleDownloadComplete(backupId, data);
    } else if (data.type === 'status') {
      this.handleStatusUpdate(backupId, data);
    } else if (data.type === 'paused') {
      this.handleDownloadPaused(backupId, data);
    } else if (data.type === 'cancelled') {
      this.handleDownloadCancelled(backupId);
    } else if (data.progress !== undefined) {
      // Legacy format
      this.handleProgressUpdate(backupId, data);
    } else if (data.success === true) {
      this.handleDownloadComplete(backupId, data);
    } else if (data.success === false) {
      this.handleDownloadError(backupId, data.error);
    }
  }

  /**
   * Handle progress updates
   */
  private handleProgressUpdate(backupId: number, data: any): void {
    const updateData: any = {
      progress: data.progress || 0,
      downloadedBytes: data.downloadedBytes || 0,
      totalBytes: data.total || 0,
      status: 'downloading'
    };

    this.updateDownloadState(backupId, updateData);

    const callbacks = this.callbacks.get(backupId);
    if (callbacks && callbacks.onProgress) {
      const currentState = this.downloadStates.get(backupId);
      if (currentState) {
        callbacks.onProgress(currentState);
      }
    }
  }

  /**
   * Handle download completion
   */
  private handleDownloadComplete(backupId: number, data: any): void {
    // Check if we should use native download for large files
    if (data.useNativeDownload) {
      // console.log('Using native download for large file');

      // Use browser's native download mechanism
      const link = document.createElement('a');
      link.href = data.downloadUrl;
      link.download = data.filename;
      link.style.display = 'none';

      // Add proper attributes
      link.setAttribute('rel', 'noopener');
      link.setAttribute('target', '_blank');

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (data.chunk) {
      // Regular blob download for smaller files
      if (data.chunk.size === 0) {
        // console.error('Received empty blob for download');
        this.handleDownloadError(backupId, 'Downloaded file is empty');
        return;
      }
      this.triggerBrowserDownload(data.chunk, data.filename);
    }

    this.updateDownloadState(backupId, {
      status: 'completed',
      progress: 100
    });

    this.cleanupWorker(backupId);

    const callbacks = this.callbacks.get(backupId);
    if (callbacks && callbacks.onComplete) {
      callbacks.onComplete(backupId);
    }

    const self = this;
    setTimeout(function() {
      self.downloadStates.delete(backupId);
      self.callbacks.delete(backupId);
      self.notifyStateChange();
    }, 5000);
  }

  /**
   * Handle download errors
   */
  private handleDownloadError(backupId: number, error: string): void {
    this.updateDownloadState(backupId, {
      status: 'error',
      error: error || 'Unknown download error',
      canResume: true
    });

    this.cleanupWorker(backupId);

    const callbacks = this.callbacks.get(backupId);
    if (callbacks && callbacks.onError) {
      callbacks.onError(backupId, error);
    }
  }

  /**
   * Handle status updates
   */
  private handleStatusUpdate(backupId: number, data: any): void {
    if (data.isActive) {
      this.updateDownloadState(backupId, {
        downloadedBytes: data.downloadedBytes,
        totalBytes: data.total,
        progress: data.total ? Math.floor((data.downloadedBytes / data.total) * 100) : 0,
        status: 'downloading'
      });
    } else if (data.canResume) {
      this.updateDownloadState(backupId, {
        downloadedBytes: data.downloadedBytes,
        totalBytes: data.total,
        progress: data.total ? Math.floor((data.downloadedBytes / data.total) * 100) : 0,
        status: 'paused',
        canResume: true
      });
    }
  }

  /**
   * Handle download paused
   */
  private handleDownloadPaused(backupId: number, data: any): void {
    this.updateDownloadState(backupId, {
      status: 'paused',
      downloadedBytes: data.downloadedBytes || 0,
      canResume: true
    });

    this.cleanupWorker(backupId);
  }

  /**
   * Handle download cancelled
   */
  private handleDownloadCancelled(backupId: number): void {
    this.updateDownloadState(backupId, { status: 'cancelled' });
    this.cleanupWorker(backupId);
  }

  /**
   * Handle worker errors
   */
  private handleWorkerError(backupId: number, error: ErrorEvent): void {
    console.error('Worker error for backup ' + backupId + ':', error);
    this.handleDownloadError(backupId, error.message || 'Worker error');
  }

  /**
   * Trigger browser download
   */
  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = objectUrl;
    link.download = filename;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(function() {
      URL.revokeObjectURL(objectUrl);
    }, 1000);
  }

  /**
   * Update download state
   */
  private updateDownloadState(backupId: number, updateData: any): void {
    const existingState = this.downloadStates.get(backupId);
    const newState: DownloadProgress = Object.assign({}, existingState, updateData, { backupId: backupId });

    this.downloadStates.set(backupId, newState);
    this.notifyStateChange();
  }

  /**
   * Notify state changes
   */
  private notifyStateChange(): void {
    this.downloadsSubject.next(new Map(this.downloadStates));
  }

  /**
   * Clean up worker
   */
  private cleanupWorker(backupId: number): void {
    const worker = this.workers.get(backupId);
    if (worker) {
      worker.terminate();
      this.workers.delete(backupId);
    }
  }

  /**
   * Check for active downloads
   */
  private checkForActiveDownloads(): void {
    const entries = Array.from(this.workers.entries());
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const backupId = entry[0];
      const worker = entry[1];
      worker.postMessage({ type: 'queryState' });
    }

    this.checkIndexedDBForOrphanedDownloads();
  }

  /**
   * Check IndexedDB for orphaned downloads
   */
  private checkIndexedDBForOrphanedDownloads(): void {
    try {
      const tempWorkerUrl = this.WORKER_URL + '?v=' + this.WORKER_VERSION + '&t=' + Date.now();
      const tempWorker = new Worker(tempWorkerUrl);

      const self = this;
      tempWorker.onmessage = function(event) {
        const data = event.data;
        if (data.type === 'status' && data.canResume && !self.workers.has(data.backupId)) {
          self.updateDownloadState(data.backupId, {
            downloadedBytes: data.downloadedBytes,
            totalBytes: data.total,
            progress: data.total ? Math.floor((data.downloadedBytes / data.total) * 100) : 0,
            status: 'paused',
            canResume: true
          });
        }
        tempWorker.terminate();
      };

      tempWorker.onerror = function() {
        tempWorker.terminate();
      };

      tempWorker.postMessage({ type: 'queryState' });

    } catch (error) {
      console.error('Error checking for orphaned downloads:', error);
    }
  }

  /**
   * Start background monitoring
   */
  private startBackgroundMonitoring(): void {
    if (this.backgroundCheckInterval) { return; }

    const self = this;
    this.backgroundCheckInterval = setInterval(function() {
      if (!document.hidden) {
        self.checkForActiveDownloads();
      }
    }, 30000);
  }

  /**
   * Stop background monitoring
   */
  private stopBackgroundMonitoring(): void {
    if (this.backgroundCheckInterval) {
      clearInterval(this.backgroundCheckInterval);
      this.backgroundCheckInterval = null;
    }
  }
}
